import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { LogoWide } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function LoginPage() {
  const { user, login, register, formatApiErrorDetail } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(false); // after register
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Giriş başarılı");
        nav("/");
      } else {
        // register — server returns { pending_verification: true } if unverified flow
        const { data } = await api.post("/auth/register", { email, password, name });
        if (data?.pending_verification) {
          toast.success("Doğrulama kodu e-postanıza gönderildi");
          setPendingVerify(true);
        } else if (data?.token) {
          if (data.token) localStorage.setItem("ztan_token", data.token);
          window.location.href = "/";
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bir hata oluştu");
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setVerifyError("");
    try {
      const { data } = await api.post("/auth/verify-email", { email, code: verifyCode });
      if (data?.token) localStorage.setItem("ztan_token", data.token);
      toast.success("E-posta doğrulandı, giriş yapıldı");
      window.location.href = "/";
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Kod hatalı";
      setVerifyError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    try {
      await api.post("/auth/resend-code", { email });
      toast.success("Yeni kod gönderildi");
    } catch { toast.error("Gönderilemedi"); }
  };

  return (
    <div className="min-h-screen bg-bg-base flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative radial glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-brand-blue/10 blur-3xl" />
      </div>

      <div className="w-full max-w-md bg-bg-panel border border-bg-border rounded-2xl p-6 sm:p-8 shadow-2xl relative z-10">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="w-[70%] mx-auto flex justify-center bg-white/95 rounded-lg p-3">
            <LogoWide className="w-full h-auto object-contain" />
          </div>
          <p
            className="text-xs sm:text-sm text-text-secondary text-center max-w-xs mt-1"
            style={{ fontFamily: "'Rajdhani', sans-serif", letterSpacing: "0.02em" }}
          >
            BIST Teknik Tarama & Piyasa Analiz Terminali
          </p>
        </div>

        <div className="flex mb-6 gap-1 bg-bg-base p-1 rounded-lg border border-bg-border">
          <button
            data-testid="auth-mode-login"
            onClick={() => { setMode("login"); setPendingVerify(false); }}
            className={`flex-1 py-2 text-sm rounded-md transition-colors ${
              mode === "login" && !pendingVerify ? "bg-brand-blue text-white" : "text-text-secondary"
            }`}
          >
            Giriş Yap
          </button>
          <button
            data-testid="auth-mode-register"
            onClick={() => { setMode("register"); setPendingVerify(false); }}
            className={`flex-1 py-2 text-sm rounded-md transition-colors ${
              mode === "register" && !pendingVerify ? "bg-brand-blue text-white" : "text-text-secondary"
            }`}
          >
            Kayıt Ol
          </button>
        </div>

        {pendingVerify ? (
          <form onSubmit={submitVerify} className="space-y-4" data-testid="verify-form">
            <div className="text-xs text-text-secondary bg-bg-base border border-bg-border rounded-lg p-3">
              <span className="text-text-primary font-medium">{email}</span> adresine 6 haneli bir kod gönderildi. Aşağıya girin.
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1.5">Doğrulama Kodu</label>
              <input
                data-testid="verify-code-input"
                value={verifyCode}
                onChange={(e) => { setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (verifyError) setVerifyError(""); }}
                required
                inputMode="numeric"
                pattern="\d{6}"
                placeholder="123456"
                className={`w-full bg-bg-base border ${verifyError ? "border-signal-down" : "border-bg-border"} text-text-primary rounded-lg px-3.5 py-2.5 text-lg font-mono tracking-[0.6em] text-center focus:border-brand-blue focus:outline-none`}
              />
              {verifyError && (
                <div data-testid="verify-error" className="mt-1.5 text-xs text-signal-down">
                  {verifyError}
                </div>
              )}
            </div>
            <button
              data-testid="verify-submit-btn"
              type="submit"
              disabled={busy || verifyCode.length !== 6}
              className="w-full bg-accent-yellow text-bg-base font-bold py-2.5 rounded-lg hover:opacity-90 text-sm disabled:opacity-60"
            >{busy ? "Doğrulanıyor..." : "Kodu Doğrula & Giriş Yap"}</button>
            <button type="button" onClick={resendCode} className="w-full text-xs text-brand-blueLight hover:underline">
              Kodu tekrar gönder
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-xs text-text-secondary mb-1.5">Ad Soyad</label>
              <input
                data-testid="auth-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3.5 py-2.5 text-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                placeholder="İsminiz"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">E-posta</label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3.5 py-2.5 text-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
              placeholder="ornek@eposta.com"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Şifre</label>
            <div className="relative">
              <input
                data-testid="auth-password-input"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg pl-3.5 pr-11 py-2.5 text-sm focus:border-brand-blue focus:outline-none focus:ring-1 focus:ring-brand-blue"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="auth-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-text-secondary hover:text-text-primary transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-accent-yellow text-bg-base font-bold py-2.5 rounded-lg hover:opacity-90 active:scale-[0.99] transition-all text-sm disabled:opacity-60"
          >
            {busy ? "İşleniyor..." : mode === "login" ? "Giriş Yap" : "Hesap Oluştur"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
